import { shortDay } from './when';
import type { PortfolioRow } from './portfolio';
import type { Project } from './projects';

/**
 * ONE BOARD ROW'S HEALTH SIGNAL in the switcher's board picker — its dot and its
 * one-line "where it stands" sub-line.
 *
 * The dot is a REDUNDANT cue: the sub-line's own word ("At risk", "Off track")
 * and its colour carry the same state, so the row never leans on colour alone
 * (R3/P7). The grade itself is NOT computed here — it is read straight off the
 * board's derived roll-up row (`PortfolioRow`, the same `s.portfolio` the
 * Portfolio screen shows), so the picker's dot can never disagree with the
 * Portfolio screen's. `h` is absent until the background roll-up load lands; the
 * row still says something true and cheap (its delivery shape and open count),
 * and the health fills in a beat later.
 *
 * Kept out of the component so it can be unit-tested with real controls — a
 * green board must read "On track", and a value with no graded health must NOT
 * invent one.
 */
export interface RowSig {
  tone: '' | 'amber' | 'red';
  dot: 'h-green' | 'h-amber' | 'h-red' | 'h-grey';
  text: string;
}

export function boardSignal(p: Project, h: PortfolioRow | undefined, open: number | null): RowSig {
  const openText = open == null ? '' : `${open} open card${open === 1 ? '' : 's'}`;
  const serviceBase = p.deliveryMode === 'continuous' ? 'Service board' : 'Project';
  if (!h) {
    return { tone: '', dot: 'h-grey', text: openText ? `${serviceBase} · ${openText}` : serviceBase };
  }
  if (h.onHold) {
    const why = h.holdReason?.trim();
    return { tone: '', dot: 'h-grey', text: why ? `On hold · ${why}` : 'On hold' };
  }
  const why = h.healthWhy?.trim() || h.waiting[0]?.label?.trim() || '';
  if (h.health === 'red') return { tone: 'red', dot: 'h-red', text: why ? `Off track · ${why}` : 'Off track' };
  if (h.health === 'amber') return { tone: 'amber', dot: 'h-amber', text: why ? `At risk · ${why}` : 'At risk' };
  if (h.health === 'green') {
    // Always LEAD with the state word, so the green state is carried by the word
    // too (never the dot's colour alone — P7), and never shares its wording with
    // an ungraded row. The count, go-live or progress is the qualifier after it.
    //
    // An IN-SERVICE board (handed over, or run as a continuous service) is PAST its
    // go-live and its delivery progress is spent — neither belongs on its sub-line.
    // It is qualified by its live work (open cards), never a "Go-Live" date that
    // reads as a still-to-come target (F3). In-delivery projects still lead with
    // their go-live below.
    if (h.tier === 'inservice') {
      return { tone: '', dot: 'h-green', text: openText ? `On track · ${openText}` : 'On track · In service' };
    }
    if (h.tracked && (h.forecastDay || h.planDay)) {
      return { tone: '', dot: 'h-green', text: `On track · Go-Live ${shortDay(h.forecastDay ?? h.planDay!)}` };
    }
    if (h.tracked) return { tone: '', dot: 'h-green', text: `On track · ${Math.round(h.progress)}% done` };
    return { tone: '', dot: 'h-green', text: openText ? `On track · ${openText}` : 'On track' };
  }
  // No graded health yet (no pulse, or not yet run as a project). A grey dot, and
  // a word that names the shape — never a bare count, which a green row also uses.
  if (p.deliveryMode === 'continuous') {
    return { tone: '', dot: 'h-grey', text: openText ? `Service board · ${openText}` : 'Service board' };
  }
  if (h.planDay) return { tone: '', dot: 'h-grey', text: `Planning · Go-Live ${shortDay(h.planDay)}` };
  return { tone: '', dot: 'h-grey', text: openText ? `Planning · ${openText}` : 'Planning' };
}
